import { Business, BusinessCenter, Info, KeyboardArrowDown, Refresh, Store } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { useState } from 'react';
import { useMicrospecDynamicUIContext } from '../../GlobalProvider';
import { useMicrospecThemeLanguageContext } from '../../Themes';
import { useOrganizationContext } from './OrganizationContextProvider';

/**
 * Organization Context Selector Component
 * Displays dropdown selectors for organization and branch context
 * Shows appropriate messages when user has no memberships
 */
const OrganizationContextSelector = ({ compact = false }) => {
  const theme = useTheme();
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { user } = cloudProvider;
  const {
    organizations,
    branches,
    selectedOrganization,
    selectedBranch,
    loading,
    error,
    hasMemberships,
    setSelectedOrganization,
    setSelectedBranch,
    refresh
  } = useOrganizationContext();

  const { localizationProvider } = useMicrospecThemeLanguageContext();

  // State for compact mode menus
  const [organizationMenuAnchor, setOrganizationMenuAnchor] = useState(null);
  const [branchMenuAnchor, setBranchMenuAnchor] = useState(null);

  if (!user) {
    return null; // Don't render if user is not logged in
  }

  // Handlers for compact mode menus
  const handleOrganizationMenuOpen = (event) => {
    setOrganizationMenuAnchor(event.currentTarget);
  };

  const handleOrganizationMenuClose = () => {
    setOrganizationMenuAnchor(null);
  };

  const handleOrganizationSelect = (organization) => {
    if (selectedOrganization?.id === organization.id) {
      handleOrganizationMenuClose();
      return;
    }
    setSelectedOrganization(organization);
    handleOrganizationMenuClose();
    window.location.reload();
  };

  const handleBranchMenuOpen = (event) => {
    setBranchMenuAnchor(event.currentTarget);
  };

  const handleBranchMenuClose = () => {
    setBranchMenuAnchor(null);
  };

  const handleBranchSelect = (branch) => {
    if (selectedBranch?.id === branch.id) {
      handleBranchMenuClose();
      return;
    }
    setSelectedBranch(branch);
    handleBranchMenuClose();
    window.location.reload();
  };

  // Compact view - organization and branch on separate lines
  if (compact) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            px: 2,
            py: 1,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: theme.palette.primary.main }} />
          ) : hasMemberships ? (
            <>
              {/* Organization line */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title={localizationProvider.translateByType({ type: 'Internal', text: 'selectOrganization' })}>
                  <Business sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                </Tooltip>
                <Chip
                  label={selectedOrganization?.name || localizationProvider.translateByType({ type: 'Internal', text: 'selectOrganization' })}
                  size="small"
                  variant="outlined"
                  onClick={handleOrganizationMenuOpen}
                  onDelete={handleOrganizationMenuOpen}
                  deleteIcon={<KeyboardArrowDown />}
                  sx={{
                    cursor: 'pointer',
                    width: '100%',
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    }
                  }}
                />
              </Box>

              {/* Branch line */}
              {selectedOrganization && branches.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title={localizationProvider.translateByType({ type: 'Internal', text: 'selectBranch' })}>
                    <Store sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                  </Tooltip>
                  <Chip
                    label={selectedBranch?.name || localizationProvider.translateByType({ type: 'Internal', text: 'selectBranch' })}
                    size="small"
                    variant="outlined"
                    onClick={handleBranchMenuOpen}
                    onDelete={handleBranchMenuOpen}
                    deleteIcon={<KeyboardArrowDown />}
                    sx={{
                      cursor: 'pointer',
                      width: '100%',
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      }
                    }}
                  />
                </Box>
              )}
            </>
          ) : (
            <Chip
              icon={<Info />}
              label={localizationProvider.translateByType({ type: 'Internal', text: 'noOrganizationMembership' })}
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
        </Box>

        {/* Organization Menu */}
        <Menu
          anchorEl={organizationMenuAnchor}
          open={Boolean(organizationMenuAnchor)}
          onClose={handleOrganizationMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              maxHeight: 300,
              minWidth: 250,
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[8],
            }
          }}
        >
          {organizations.map((organization) => (
            <MenuItem
              key={organization.id}
              selected={selectedOrganization?.id === organization.id}
              onClick={() => handleOrganizationSelect(organization)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.action.selected,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  }
                }
              }}
            >
              <ListItemIcon>
                <Business fontSize="small" sx={{ color: theme.palette.text.secondary }} />
              </ListItemIcon>
              <ListItemText
                primary={organization.name}
                secondary={organization.roles && organization.roles.length > 0 ? `${organization.roles.length} ${localizationProvider.translateByType({ type: 'Internal', text: 'roles' })}` : null}
                primaryTypographyProps={{ color: theme.palette.text.primary }}
                secondaryTypographyProps={{ color: theme.palette.text.secondary }}
              />
            </MenuItem>
          ))}
        </Menu>

        {/* Branch Menu */}
        <Menu
          anchorEl={branchMenuAnchor}
          open={Boolean(branchMenuAnchor)}
          onClose={handleBranchMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              maxHeight: 300,
              minWidth: 250,
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[8],
            }
          }}
        >
          {branches.map((branch) => (
            <MenuItem
              key={branch.id}
              selected={selectedBranch?.id === branch.id}
              onClick={() => handleBranchSelect(branch)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.action.selected,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  }
                }
              }}
            >
              <ListItemIcon>
                <Store fontSize="small" sx={{ color: theme.palette.text.secondary }} />
              </ListItemIcon>
              <ListItemText
                primary={branch.name}
                secondary={branch.location?.city}
                primaryTypographyProps={{ color: theme.palette.text.primary }}
                secondaryTypographyProps={{ color: theme.palette.text.secondary }}
              />
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  // Full view - card with dropdowns
  if (loading) {
    return (
      <Card sx={{ backgroundColor: theme.palette.background.paper }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
            <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
            <Typography variant="body1" sx={{ ml: 2, color: theme.palette.text.primary }}>
              {localizationProvider.translateByType({ type: 'Internal', text: 'loadingOrganizationContext' })}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ backgroundColor: theme.palette.background.paper }}>
        <CardContent>
          <Alert
            severity="error"
            sx={{
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(211, 47, 47, 0.15)'
                : 'rgba(211, 47, 47, 0.1)',
              color: theme.palette.text.primary,
              '& .MuiAlert-icon': {
                color: theme.palette.error.main,
              }
            }}
            action={
              <Button
                size="small"
                onClick={refresh}
                startIcon={<Refresh />}
                sx={{
                  color: theme.palette.error.main,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(211, 47, 47, 0.2)'
                      : 'rgba(211, 47, 47, 0.15)',
                  }
                }}
              >
                {localizationProvider.translateByType({ type: 'Internal', text: 'retry' })}
              </Button>
            }
          >
            <Typography sx={{ color: theme.palette.text.primary }}>{error}</Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!hasMemberships) {
    return (
      <Card sx={{ backgroundColor: theme.palette.background.paper }}>
        <CardContent>
          <Alert
            severity="info"
            icon={<BusinessCenter sx={{ color: theme.palette.info.main }} />}
            sx={{
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(2, 136, 209, 0.15)'
                : 'rgba(2, 136, 209, 0.1)',
              color: theme.palette.text.primary,
              '& .MuiAlert-icon': {
                color: theme.palette.info.main,
              }
            }}
          >
            <Typography variant="subtitle2" gutterBottom sx={{ color: theme.palette.text.primary }}>
              {localizationProvider.translateByType({ type: 'Internal', text: 'noOrganizationMembership' })}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {localizationProvider.translateByType({ type: 'Internal', text: 'noOrganizationMembershipDescription' })}
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{
      backgroundColor: theme.palette.background.paper,
      '& .MuiCardContent-root': { py: 1, px: 2, '&:last-child': { pb: 1.5 } }
    }}>
      <CardContent>
        <Stack spacing={1.5}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600, color: theme.palette.text.primary }}>
              <BusinessCenter sx={{
                color: theme.palette.mode === 'dark' ?
                  "#fff" :
                  theme.palette.primary.main,
                fontSize: 18
              }} />
              {localizationProvider.translateByType({ type: 'Internal', text: 'workspaceContext' })}
            </Typography>
            <Tooltip title={localizationProvider.translateByType({ type: 'Internal', text: 'refresh' })}>
              <IconButton
                size="small"
                onClick={refresh}
                sx={{
                  ml: 1,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.primary.main,
                  }
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Organization Selector */}
          <FormControl
            fullWidth
            size="small"
            sx={{
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
                '&.Mui-focused': {
                  color: theme.palette.primary.main,
                }
              }
            }}
          >
            <InputLabel>
              {localizationProvider.translateByType({ type: 'Internal', text: 'selectOrganization' })}
            </InputLabel>
            <Select
              value={selectedOrganization?.id || ''}
              label={localizationProvider.translateByType({ type: 'Internal', text: 'selectOrganization' })}
              onChange={(e) => {
                const organization = organizations.find(b => b.id === e.target.value);
                setSelectedOrganization(organization);
              }}
              sx={{
                backgroundColor: theme.palette.background.paper,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.divider,
                  transition: 'border-color 0.2s ease',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2,
                },
                '& .MuiSelect-icon': {
                  color: theme.palette.text.secondary,
                  transition: 'color 0.2s ease',
                },
                '&:hover .MuiSelect-icon': {
                  color: theme.palette.primary.main,
                },
                '& .MuiInputBase-input': {
                  color: theme.palette.text.primary,
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: theme.shadows[8],
                    '& .MuiMenuItem-root': {
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.action.selected,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        }
                      }
                    }
                  }
                }
              }}
              renderValue={(value) => {
                const org = organizations.find(o => o.id === value);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Business sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>{org?.name || ''}</Typography>
                  </Box>
                );
              }}
            >
              {organizations.map((organization) => (
                <MenuItem key={organization.id} value={organization.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Business sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>{organization.name}</Typography>
                    {organization.roles && organization.roles.length > 0 && (
                      <Chip
                        label={`${organization.roles.length} ${localizationProvider.translateByType({ type: 'Internal', text: 'roles' })}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          borderColor: theme.palette.divider,
                          color: theme.palette.text.secondary,
                          backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.02)',
                        }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Branch Selector */}
          {selectedOrganization && branches.length > 0 && (
            <FormControl
              fullWidth
              size="small"
              sx={{
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.secondary,
                  '&.Mui-focused': {
                    color: theme.palette.primary.main,
                  }
                }
              }}
            >
              <InputLabel>
                {localizationProvider.translateByType({ type: 'Internal', text: 'selectBranch' })}
              </InputLabel>
              <Select
                value={selectedBranch?.id || ''}
                label={localizationProvider.translateByType({ type: 'Internal', text: 'selectBranch' })}
                onChange={(e) => {
                  const branch = branches.find(b => b.id === e.target.value);
                  setSelectedBranch(branch);
                }}
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                    transition: 'border-color 0.2s ease',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                  },
                  '& .MuiSelect-icon': {
                    color: theme.palette.text.secondary,
                    transition: 'color 0.2s ease',
                  },
                  '&:hover .MuiSelect-icon': {
                    color: theme.palette.primary.main,
                  },
                  '& .MuiInputBase-input': {
                    color: theme.palette.text.primary,
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: theme.palette.background.paper,
                      boxShadow: theme.shadows[8],
                      '& .MuiMenuItem-root': {
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.action.selected,
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                          }
                        }
                      }
                    }
                  }
                }}
                renderValue={(value) => {
                  const branch = branches.find(b => b.id === value);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Store sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                      <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>{branch?.name || ''}</Typography>
                    </Box>
                  );
                }}
              >
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Store sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                      <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>{branch.name}</Typography>
                      {branch.location?.city && (
                        <Chip
                          label={branch.location.city}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.secondary,
                            backgroundColor: theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(0, 0, 0, 0.02)',
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Organization Context Badge */}
          {selectedOrganization && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                textAlign: 'center',
                justifyContent: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                backgroundColor: theme.palette.mode === 'dark'
                  ? `${theme.palette.primary.main}20`
                  : `${theme.palette.primary.main}18`,
                border: `1px solid ${theme.palette.mode === 'dark'
                  ? `${theme.palette.primary.main}60`
                  : `${theme.palette.primary.main}40`}`,
              }}
            >
              <Typography variant="caption" sx={{
                color: theme.palette.mode === 'dark' ?
                  "#fff" :
                  theme.palette.primary.main,
                fontWeight: 500
              }}>
                {selectedOrganization.name}
              </Typography>
              {selectedBranch && (
                <>
                  <Typography variant="caption" sx={{
                    color: theme.palette.mode === 'dark' ?
                      "#fff" :
                      theme.palette.text.secondary
                  }}>→</Typography>
                  <Typography variant="caption" sx={{
                    color: theme.palette.mode === 'dark' ?
                      "#fff" :
                      theme.palette.primary.main,
                    fontWeight: 500
                  }}>
                    {selectedBranch.name}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default OrganizationContextSelector;
